import { useSyncExternalStore } from 'react'

/**
 * Helm as an installed app: registers the service worker, and keeps the
 * browser's install offer for the "Install Helm" button instead of letting the
 * browser show its own banner at a random moment.
 */

interface InstallOffer extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let offer: InstallOffer | null = null
const listeners = new Set<() => void>()

function setOffer(next: InstallOffer | null) {
  offer = next
  for (const listener of listeners) listener()
}

// Dev servers and browser tests run without the worker, so they always load fresh code.
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]'])

/** Call once, before React mounts: the browser makes its offer early. */
export function setUpInstalledApp() {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    setOffer(event as InstallOffer)
  })
  window.addEventListener('appinstalled', () => setOffer(null))

  if ('serviceWorker' in navigator && !LOCAL_HOSTS.has(window.location.hostname)) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').catch(() => {
        // Helm works the same without it; only the offline page and install are lost.
      })
    })
  }
}

/** The browser's install offer, when it has made one (Chrome, Edge, Android). */
export function useInstallOffer() {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => offer,
    () => null,
  )
}

/** Shows the browser's install dialog. An offer can be used once. */
export async function acceptInstallOffer() {
  const current = offer
  if (!current) return false
  setOffer(null)
  await current.prompt()
  return (await current.userChoice).outcome === 'accepted'
}

export function isRunningInstalled() {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

/** iPhone and iPad make no install offer: people add Helm from the Share menu. */
export function isAppleMobile() {
  const agent = navigator.userAgent
  return /iPhone|iPad|iPod/.test(agent) || (/Macintosh/.test(agent) && navigator.maxTouchPoints > 1)
}
